<template>
  <div class="s-audioPlayer">
    <button
      class="s-audioPlayer__button o-unstyled"
      :aria-label="isPlayingThis ? $t('generic.pause') : $t('generic.play')"
      @click.stop="onTogglePlay">
      <svg
        v-if="!isPlayingThis"
        class="s-audioPlayer__icon"
        viewBox="0 0 48 48"
        aria-hidden="true">
        <circle
          cx="24"
          cy="24"
          r="24"
          class="s-audioPlayer__iconBg" />
        <polygon
          points="20,16 20,32 34,24"
          class="s-audioPlayer__iconFg -play" />
      </svg>
      <svg
        v-else
        class="s-audioPlayer__icon"
        viewBox="0 0 48 48"
        aria-hidden="true">
        <circle
          cx="24"
          cy="24"
          r="24"
          class="s-audioPlayer__iconBg" />
        <rect
          x="18"
          y="16"
          width="4"
          height="16"
          rx="1"
          class="s-audioPlayer__iconFg" />
        <rect
          x="26"
          y="16"
          width="4"
          height="16"
          rx="1"
          class="s-audioPlayer__iconFg" />
      </svg>
    </button>

    <div
      ref="waveform"
      class="s-audioPlayer__waveform"
      role="slider"
      tabindex="0"
      :aria-valuemin="0"
      :aria-valuemax="1"
      :aria-valuenow="displayProgress"
      @click="onSeek"
      @keydown="onWaveformKeydown">
      <AudioProgressVisualization
        :volumes="volumes"
        :width="containerWidth"
        :progress="displayProgress" />
    </div>

    <div class="s-audioPlayer__time t-caption1 u-colorDarkLightEmphasis">
      {{ formattedCurrent }} / {{ formattedDuration }}
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import {
  computed, ref, onMounted, onBeforeUnmount, watch, toRefs,
} from 'vue'

import AudioProgressVisualization from '@/components/AudioProgressVisualization.vue'
import { useAudioPlayerStore } from '@/modules/global/audioPlayerStore'

const props = defineProps({
  audioUrl: { type: String, required: true },
  volumes: { type: Array, required: true },
  durationSeconds: { type: Number, default: 0 },
})
const { audioUrl, volumes, durationSeconds } = toRefs(props)

const store = useAudioPlayerStore()
const {
  currentUrl, isPlaying, progress, currentTimeSec,
} = storeToRefs(store)
const waveform = ref(null)
const containerWidth = ref(0)
const isPlayingThis = computed(() => isPlaying.value && currentUrl.value === audioUrl.value)

// Displayed current time: use global store when this player is active, otherwise use per-URL saved time
const displayCurrentSeconds = computed(() => {
  if (isPlayingThis.value) {
    return currentTimeSec.value
  }
  return store.timeByUrl[audioUrl.value] || 0
})

// Return the progress of the current file as a value between 0 and 1.
const displayProgress = computed(() => {
  if (isPlayingThis.value) {
    return progress.value
  }
  const total = durationSeconds.value
  if (!Number.isFinite(total) || total <= 0) {
    return 0
  }
  const currentTimeByUrl = store.timeByUrl[audioUrl.value] || 0
  const ratio = currentTimeByUrl / total
  return Math.min(1, Math.max(0, ratio))
})

function onTogglePlay() {
  store.play(audioUrl.value, undefined, durationSeconds.value)
}

// We allow the user to jump to a specific time in the audio by clicking on the waveform.
function onSeek(event) {
  if (!waveform.value) {
    return
  }
  const rect = waveform.value.getBoundingClientRect()
  if (!rect || !Number.isFinite(rect.width) || rect.width <= 0) {
    return
  }
  const clickOffsetX = event.clientX - rect.left
  const progressRatio = clickOffsetX / rect.width
  if (isPlayingThis.value) {
    const total = durationSeconds.value
    const seconds = total > 0 ? Math.max(0, Math.min(total, progressRatio * total)) : 0
    store.seekToSeconds(seconds)
    return
  }
  // Plan a seek for this URL and start playing it. Use provided durationSeconds when available.
  const total = durationSeconds.value
  const seconds = total > 0 ? Math.max(0, Math.min(total, progressRatio * total)) : 0
  store.play(audioUrl.value, seconds, durationSeconds.value)
}

// A11y: keyboard controls on waveform slider
function onWaveformKeydown(event) {
  if (event.key === 'ArrowLeft') {
    event.preventDefault()
    if (isPlayingThis.value) {
      const step = 0.05
      const total = durationSeconds.value
      const seconds = Math.max(0, displayCurrentSeconds.value - (step * total))
      store.seekToSeconds(seconds)
    } else {
      const step = 0.05
      const total = durationSeconds.value
      const newRatio = Math.max(0, displayProgress.value - step)
      const seconds = total > 0 ? newRatio * total : 0
      store.play(audioUrl.value, seconds, durationSeconds.value)
    }
    return
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault()
    if (isPlayingThis.value) {
      const step = 0.05
      const total = durationSeconds.value
      const seconds = Math.max(0, Math.min(total, displayCurrentSeconds.value + (step * total)))
      store.seekToSeconds(seconds)
    } else {
      const step = 0.05
      const total = durationSeconds.value
      const newRatio = Math.min(1, displayProgress.value + step)
      const seconds = total > 0 ? newRatio * total : 0
      store.play(audioUrl.value, seconds, durationSeconds.value)
    }
    return
  }
  if (event.key === 'Home') {
    event.preventDefault()
    if (isPlayingThis.value) {
      store.seekToSeconds(0)
    } else {
      store.play(audioUrl.value, 0, durationSeconds.value)
    }
    return
  }
  if (event.key === 'End') {
    event.preventDefault()
    if (isPlayingThis.value) {
      const total = durationSeconds.value
      store.seekToSeconds(total || 0)
    } else {
      const total = durationSeconds.value
      store.play(audioUrl.value, total, durationSeconds.value)
    }
    return
  }
  if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault()
    onTogglePlay()
  }
}

function updateWidth() {
  if (waveform.value) {
    containerWidth.value = Math.floor(waveform.value.clientWidth)
  }
}

let resizeObserver = null
let animationFrameId = null
function animationLoop() {
  const audioElement = store.getAudioEl()
  if (!audioElement) {
    animationFrameId = null
    return
  }
  const currentSeconds = Number.isFinite(audioElement.currentTime) && audioElement.currentTime >= 0 ? audioElement.currentTime : 0
  let progressRatio = 0
  if (durationSeconds.value > 0) {
    progressRatio = Math.min(1, Math.max(0, currentSeconds / durationSeconds.value))
  }
  // Keep store in sync for other UI (time display, aria, etc.)
  currentTimeSec.value = currentSeconds
  progress.value = progressRatio

  if (!audioElement.paused && !audioElement.ended && isPlayingThis.value) {
    animationFrameId = requestAnimationFrame(animationLoop)
  } else {
    animationFrameId = null
  }
}
function startAnimationLoop() {
  if (animationFrameId != null) {
    return
  }
  animationFrameId = requestAnimationFrame(animationLoop)
}
function stopAnimationLoop() {
  if (animationFrameId != null) {
    cancelAnimationFrame(animationFrameId)
    animationFrameId = null
  }
}

onMounted(() => {
  updateWidth()
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const newWidth = Math.floor(entry.contentRect.width)
      if (newWidth !== containerWidth.value) {
        containerWidth.value = newWidth
      }
    }
  })
  if (waveform.value) {
    resizeObserver.observe(waveform.value)
  }
  // Start rAF loop if already playing this audio on mount
  if (isPlayingThis.value) {
    startAnimationLoop()
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  stopAnimationLoop()
})

// Start/stop loop when this component's audio starts/stops
watch(isPlayingThis, (isPlayingNow) => {
  if (isPlayingNow) {
    startAnimationLoop()
  } else {
    stopAnimationLoop()
  }
})

// Time formatting (capped to 99:99 to keep width stable)
const formatTime = (totalSeconds) => {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return '00:00'
  }
  const minutesValue = Math.min(99, Math.max(0, Math.floor(totalSeconds / 60)))
  const secondsValue = Math.min(99, Math.max(0, Math.floor(totalSeconds % 60)))
  return `${String(minutesValue).padStart(2, '0')}:${String(secondsValue).padStart(2, '0')}`
}
const formattedCurrent = computed(() => formatTime(displayCurrentSeconds.value))
const formattedDuration = computed(() => formatTime(durationSeconds.value))
</script>

<style lang="scss" scoped>
@import '@/assets/scss/config';

.s-audioPlayer {
  align-items: center;
  display: flex;
  gap: $spacing-3;
  min-width: 0;
  max-width: 100%;
}

.s-audioPlayer__button {
  height: 40px;
  width: 40px;
}

.s-audioPlayer__icon {
  height: 40px;
  width: 40px;
}

.s-audioPlayer__iconBg {
  fill: $color-primary;
}

.s-audioPlayer__iconFg {
  fill: $color-white;

  &.-play {
    /** Visually center it */
    transform: translateX(-1px);
  }
}

.s-audioPlayer__waveform {
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 2px;
  height: 28px;
  min-width: 120px;
  width: 100%;
}

.s-audioPlayer__time {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  width: 13ch; /* this element has 13 characters: "00:00 / 00:00" */
  flex: 0 0 13ch;
  text-align: right;
}
</style>
